import { useEffect, useState, type ReactNode } from 'react';

import { telecharger } from '../api/client.ts';

/**
 * Image servie par l'API, donc protégée par le jeton.
 *
 * Une balise `<img src="/api/...">` ne transmet AUCUN en-tête : l'API
 * répondait 401 et les photos de produit restaient vides — envoyées, mais
 * jamais visibles. Le fichier est lu ici avec le jeton, puis confié à
 * l'image sous forme d'URL locale.
 *
 * L'URL locale est RÉVOQUÉE au démontage : sans cela, chaque ouverture du
 * panneau garderait une copie de l'image en mémoire jusqu'à la fermeture de
 * l'onglet.
 */
export function ImageProtegee({ chemin, alt }: { chemin: string; alt: string }): ReactNode {
  const [url, setUrl] = useState<string | null>(null);
  const [echec, setEchec] = useState(false);

  useEffect(() => {
    let annule = false;
    let locale: string | null = null;

    telecharger(chemin)
      .then((blob) => {
        if (annule) return;
        locale = URL.createObjectURL(blob);
        setUrl(locale);
      })
      .catch(() => {
        if (!annule) setEchec(true);
      });

    return () => {
      annule = true;
      if (locale !== null) URL.revokeObjectURL(locale);
    };
  }, [chemin]);

  const style = {
    width: '100%',
    aspectRatio: '4 / 3',
    objectFit: 'cover' as const,
    borderRadius: 6,
    background: 'var(--fond-appui)',
  };

  if (echec) {
    return (
      <div style={{ ...style, display: 'grid', placeItems: 'center' }} className="entete__sous">
        Image indisponible
      </div>
    );
  }
  if (url === null) {
    return <div style={style} className="squelette" aria-label="Chargement de l'image" />;
  }
  return <img src={url} alt={alt} style={style} />;
}
